import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';

const Pin = ({ className, fill, style }) => (
  <svg viewBox="0 0 24 32" className={className} style={style} aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.3 0 11.9 0 20.4 12 32 12 32s12-11.6 12-20.1C24 5.3 18.6 0 12 0z" fill={fill} />
    <circle cx="12" cy="12" r="5" fill="#fff" fillOpacity="0.92" />
  </svg>
);

export const JurisdictionEmptyState = ({ onAdd, readOnly }) => (
  <div className="tcp-empty flex-1 relative flex flex-col items-center justify-center text-center p-8 min-h-[520px]" data-testid="jurisdiction-empty-state">
    <div className="tcp-empty-blob tcp-empty-blob-a" />
    <div className="tcp-empty-blob tcp-empty-blob-b" />
    <svg className="tcp-empty-wave" viewBox="0 0 1200 220" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 140 C 180 60, 320 60, 480 130 S 800 200, 1000 120 S 1150 90, 1200 110 L1200 220 L0 220 Z" fill="#eef2ff" />
      <path d="M0 180 C 200 120, 380 130, 560 170 S 880 230, 1200 160 L1200 220 L0 220 Z" fill="#e0e7ff" fillOpacity="0.7" />
    </svg>
    <div className="tcp-empty-art tcp-fade-in relative">
      <img src="/assets/tax-empty-state.png" alt="" aria-hidden="true" className="w-[270px] h-auto select-none pointer-events-none" draggable="false" />
      <Pin className="tcp-float w-6 h-8 absolute -left-5 top-[52%]" fill="#7c3aed" />
    </div>
    <div className="relative tcp-fade-in mt-5 space-y-1.5">
      <p className="text-lg font-semibold text-slate-800" data-testid="jurisdiction-empty-title">No Jurisdiction Selected</p>
      <p className="text-sm text-slate-500 max-w-md mx-auto leading-relaxed">
        Select a jurisdiction from the list or add a new one to manage tax certificate details, records and workflow.
      </p>
    </div>
    <Button
      type="button"
      onClick={onAdd}
      disabled={readOnly}
      className="tcp-empty-cta relative mt-6 h-11 px-7 rounded-xl text-white text-[15px] font-semibold"
      data-testid="add-jurisdiction-empty-btn"
    >
      <Plus className="w-4 h-4 mr-2" />
      Add Jurisdiction
    </Button>
  </div>
);

export const JurisdictionSidebarEmpty = () => (
  <div className="tcp-side-empty relative flex-1 min-h-[420px] overflow-hidden" data-testid="jurisdiction-sidebar-empty">
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 560" preserveAspectRatio="xMidYMax meet" aria-hidden="true">
      <path d="M60 50 C 10 110, 150 160, 100 230 S 20 380, 70 430 S 150 500, 110 550" fill="none" stroke="#cbd5e1" strokeWidth="1.6" strokeDasharray="3 6" strokeLinecap="round" />
      <path d="M-20 530 C 40 480, 90 515, 130 495 S 200 470, 240 505 L240 580 L-20 580 Z" fill="#eef2ff" />
      <path d="M-20 555 C 60 525, 120 548, 240 530 L240 580 L-20 580 Z" fill="#e0e7ff" fillOpacity="0.8" />
      <g fill="#a7f3d0" fillOpacity="0.9">
        <path d="M150 470 l9 22 h-18 z" /><rect x="157.5" y="491" width="3" height="8" fill="#94a3b8" />
        <path d="M30 500 l7 17 h-14 z" /><rect x="35.8" y="516" width="2.4" height="6" fill="#94a3b8" />
      </g>
      <g className="tcp-float" style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
        <path d="M60 22 c-7 0 -12 5.3 -12 11.9 0 8.5 12 20.1 12 20.1 s12 -11.6 12 -20.1 c0 -6.6 -5 -11.9 -12 -11.9z" fill="#a78bfa" />
        <circle cx="60" cy="34" r="4.5" fill="#fff" fillOpacity="0.92" />
      </g>
      <g fill="none" stroke="#cbd5e1" strokeWidth="1.6">
        <path d="M118 198 c-6 0 -11 4.9 -11 10.9 0 7.7 11 18.1 11 18.1 s11 -10.4 11 -18.1 c0 -6 -5 -10.9 -11 -10.9z" />
        <circle cx="118" cy="209" r="4" />
      </g>
      <g className="tcp-float tcp-float-delay" style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
        <path d="M70 400 c-7 0 -12 5.3 -12 11.9 0 8.5 12 20.1 12 20.1 s12 -11.6 12 -20.1 c0 -6.6 -5 -11.9 -12 -11.9z" fill="#3b82f6" />
        <circle cx="70" cy="412" r="4.5" fill="#fff" fillOpacity="0.92" />
      </g>
    </svg>
    <div className="absolute left-0 right-0 top-[52%] px-3 text-center">
      <p className="text-sm font-medium text-slate-700">No jurisdictions added</p>
      <p className="text-xs text-slate-400 mt-1">Click &ldquo;Add Jurisdiction&rdquo; to start</p>
    </div>
  </div>
);

export const JurisdictionSidebarArt = () => (
  <div className="tcp-side-art tcp-fade-in" aria-hidden="true" data-testid="jurisdiction-sidebar-art">
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 300" preserveAspectRatio="xMidYMax meet">
      <path d="M90 10 C 40 60, 160 100, 110 150 S 40 220, 100 262" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="3 6" strokeLinecap="round" />
      <path d="M-20 262 C 40 232, 100 258, 150 240 S 200 228, 240 250 L240 320 L-20 320 Z" fill="#eef2ff" />
      <path d="M-20 290 C 60 268, 120 290, 240 274 L240 320 L-20 320 Z" fill="#e0e7ff" fillOpacity="0.8" />
      <g className="tcp-float" style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
        <path d="M90 -4 c-6 0 -11 4.9 -11 10.9 0 7.7 11 18.1 11 18.1 s11 -10.4 11 -18.1 c0 -6 -5 -10.9 -11 -10.9z" fill="#8b5cf6" />
        <circle cx="90" cy="7" r="4" fill="#fff" fillOpacity="0.92" />
      </g>
      <g className="tcp-float tcp-float-delay" style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
        <path d="M112 118 c-6 0 -11 4.9 -11 10.9 0 7.7 11 18.1 11 18.1 s11 -10.4 11 -18.1 c0 -6 -5 -10.9 -11 -10.9z" fill="#6366f1" />
        <circle cx="112" cy="129" r="4" fill="#fff" fillOpacity="0.92" />
      </g>
    </svg>
    <img src="/assets/tax-empty-state.png" alt="" draggable="false" />
  </div>
);
